import TransformNode from "./TransformNode";
import SourceNode from "./SourceNode";
import NodeSet from "./NodeSet";
import {
  assign,
  bound,
  computeArgs,
  constant,
  fromPairs,
  identity,
  invoke,
  keys,
  mapObject,
  mapObjectDeep,
  matchInvokeDefaulted,
  overArgs,
  pick,
  property,
  propertyOf,
  rest,
  toPairs,
  unrest,
  zip
} from "../voidash";
import ObserveNode from "./ObserveNode";
import Node from "./Node";
import NodeMap from "./NodeMap";
import {randomColorCSS} from "../../color-utils";

const STATE_OPEN = "open";
const STATE_CLOSED = "closed";

class NodeBagBase {
  graph;

  constructor(obj) {
    if (obj) Object.assign(this, obj);
  }

  t(keys, fn) { return this.graph.t(this.mapKeys(keys), fn); }
  o(keys, fn) { return this.graph.o(this.mapKeys(keys), fn); }
  ta(keys, fn) { return this.graph.ta(this.mapKeys(keys), fn); }
  oa(keys, fn) { return this.graph.oa(this.mapKeys(keys), fn); }
  to(keys, fn) { return this.graph.to(pick(this, keys), fn); }
  oo(keys, fn) { return this.graph.oo(pick(this, keys), fn); }
  read(keys, fn) { return this.graph.read(this.mapKeys(keys), fn); }

  tReduce(key, prev, fn) { return this[key].tReduce(prev, fn); }
  oReduce(key, prev, fn) { return this[key].oReduce(prev, fn); }

  setObject(obj) { return this.graph.setObject(this, obj) };

  mapKeys(keys) {
    if (typeof keys === "string") keys = [keys];
    return keys.map(matchInvokeDefaulted(
      k => { throw new Error(`NodeBag: unsupported argument ${k}`); },
      [k => typeof k === "string" && this[k], propertyOf(this)],
      [k => k instanceof Node, identity]
    ));
  }
}

function topSort(affectedNodes) {
  const sorted = [];
  const states = {};

  function visit(node) {
    if (!affectedNodes.has(node)) return;

    if (states[node.id] == null) {
      states[node.id] = STATE_OPEN;
      node.forEachDependence(visit);
      sorted.push(node);
      states[node.id] = STATE_CLOSED;
    } else if (states[node.id] === STATE_OPEN) {
      console.error("DataTransformGraph: Cyclic dependency detected");
    }
  }

  affectedNodes.forEach(node => {
    let hasSuccessors = false;
    node.forEachSuccessor(succ => { hasSuccessors = affectedNodes.has(succ) || hasSuccessors; });
    if (!hasSuccessors) visit(node);
  });

  return sorted;
}

function findTransitiveSuccessors(sources) {
  const affectedNodes = new NodeSet();

  function visit(node) {
    if (affectedNodes.add(node)) node.forEachSuccessor(visit);
  }

  sources.forEach(visit);

  return affectedNodes;
}

const defaults = {
  debug: false
};

export default class Graph {
  constructor(opts = {}) {
    this.opts = assign({}, defaults, opts);
    this.clearScheduledUpdates();

    const graph = this;
    this.NodeBag = class extends NodeBagBase {
      get graph() {
        return graph;
      }
    };

    this.updateCallbacks = [];
    this.createdDuringUpdate = new NodeSet();
    this.currentlyUpdatingNode = null;

    if (this.opts.debug) {
      this.changesDuringUpdate = new NodeMap();
      this.pastChangesDuringUpdate = new NodeMap();
      this.sourceNodes = [];
    }
  }

  createSource(value, pred = [], fn = identity) {
    const node = new SourceNode(this, value, pred, fn);
    if (this.opts.debug) this.sourceNodes.push(node);
    return node;
  }

  createDepSource(value, pred = [], fn = identity) {
    console.warn("Graph#createDepSource is deprecated, use Graph#createSource.");
    return this.createSource(value, pred, fn);
  }

  createTrigger(fn) {
    const node = this.createSource(undefined);
    fn(() => node.trigger());
    return node;
  }

  t(nodes, fn) { return this.ta(nodes, rest(fn)); }
  o(nodes, fn) { return this.oa(nodes, rest(fn)); }

  ta(nodes, fn) { return new TransformNode(this, nodes, fn); }
  oa(nodes, fn) { return new ObserveNode(this, nodes, fn); }

  to(obj, fn) {
    const [keys, nodes] = zip(...toPairs(obj));
    return this.ta(nodes, values => fn(fromPairs(zip(keys, values))));
  }

  oo(obj, fn) {
    const [keys, nodes] = zip(...toPairs(obj));
    return this.oa(nodes, values => fn(fromPairs(zip(keys, values))));
  }

  read(nodes, fn) {
    const observer = this.oa(nodes, args => {
      observer.deactivate();
      fn(...args);
    });
    return observer;
  }

  mapObject(obj) {
    return mapObject(obj, (v, k) => this.createSource(v).lbl(`obj ${k}`));
  }

  mapObjectDeep(obj) {
    return mapObjectDeep(obj, (v, k) => this.createSource(v).lbl(`obj ${k}`));
  }

  setObject(nodeObj, valueObj) {
    keys(valueObj).forEach(key => {
      if (nodeObj.hasOwnProperty(key)) nodeObj[key].set(valueObj[key]);
    });
  }

  update() {
    const LIMIT = 10;

    if (this.opts.debug) {
      // cleanup
      this.sourceNodes = this.sourceNodes.filter(property("active"));
    }

    for (let i = 0; i < LIMIT; i++) {
      if (this.changedSources.size() === 0) {
        this.runUpdateCallbacks();
        break;
      }

      // phase 1: find all affected nodes
      const affectedNodes = findTransitiveSuccessors(this.changedSources);

      const changedSources = this.changedSources;
      this.clearScheduledUpdates();

      // phase 2: top sort
      const sorted = topSort(affectedNodes);

      // phase 3: update
      const dirtyNodes = new NodeSet();
      const markDirty = bound(dirtyNodes, "add");
      const markSuccessorsDirty = invoke("forEachSuccessor", markDirty);

      changedSources.forEach(markDirty);

      sorted.forEach(node => {
        if (!dirtyNodes.has(node)) return;
        const prevValue = node.value;
        this.changedSources.remove(node);

        if (this.opts.debug) {
          this.changesDuringUpdate.remove(node);
        }

        this.currentlyUpdatingNode = node;
        node.update()
        this.currentlyUpdatingNode = null;

        if (node.value !== prevValue || node.value === undefined) markSuccessorsDirty(node);
      });
    }

    if (this.opts.debug && this.changesDuringUpdate.size()) {
      console.warn("DataTransformGraph: There were changes during an update.");
      this.pastChangesDuringUpdate.merge(this.changesDuringUpdate);
      this.changesDuringUpdate.clear();
    }

    this.runUpdateCallbacks();
  }

  notifyChange(node) {
    this.changedSources.add(node);

    if (this.opts.debug && this.currentlyUpdatingNode) {
      this.changesDuringUpdate.set(node, this.currentlyUpdatingNode);
    }
  }

  notifyCreate(node) {
    if (this.currentlyUpdatingNode) {
      this.createdDuringUpdate.add(node); // TODO: clear

      let isSource = true; // prevent dependencies from descendants

      node.forEachPredecessor(pred => {
        if (this.createdDuringUpdate.has(pred)) {
          isSource = false;
        }
      });

      if (isSource) {
        node.depend(this.currentlyUpdatingNode);
      }
    }
  }

  clearScheduledUpdates() {
    this.changedSources = new NodeSet();
  }

  onNextUpdate(cb) {
    this.updateCallbacks.push(cb);
  }

  runUpdateCallbacks() {
    const callbacks = this.updateCallbacks;
    this.updateCallbacks = [];
    callbacks.forEach(fn => { fn(); });
  }

  toDot() {
    if (!this.opts.debug) return "Not in debug mode";

    const visited = new NodeSet();

    const instanceOf = cls => obj => obj instanceof cls;

    const add = (a, b) => a + b;

    const prefixId = prefix => computeArgs(add, [constant(prefix), property("id")]);

    const getNodeId = prefixId("n");

    const getNodeLabel = matchInvokeDefaulted(
      getNodeId,
      [property("label"), node => `${node.label.replace(/\s+/g, "\\n")}\\n${node.id}`],
      [instanceOf(SourceNode), prefixId("s")],
      [instanceOf(TransformNode), prefixId("t")],
      [instanceOf(ObserveNode), prefixId("o")]
    );

    const getNodeShape = matchInvokeDefaulted(
      constant("plain"),
      ...[
        [SourceNode, "box"],
        [TransformNode, "diamond"],
        [ObserveNode, "oval"]
      ].map(rest(overArgs(unrest(identity), [instanceOf, constant])))
    );

    const getEdgeWeight = (node, succ) => {
      const predCnt = succ.pred.length;
      return predCnt * predCnt;
    };

    const pastChangesDuringUpdate = this.pastChangesDuringUpdate;
    let i = 0;
    const nodes = [];
    const edges = [];

    const visit = node => {
      if (visited.add(node)) {
        const nodeId = getNodeId(node);

        nodes.push(`${nodeId} [label="${getNodeLabel(node)}",shape=${getNodeShape(node)}]`);

        if (pastChangesDuringUpdate.has(node)) {
          edges.push(`${getNodeId(pastChangesDuringUpdate.get(node))} -> ${nodeId} [color=red,penwidth=5,style=dashed,label="!",fontcolor=red,fontsize="40pt"]`);
        }

        node.forEachSuccessor(succ => {
          visit(succ);
          edges.push(`${nodeId} -> ${getNodeId(succ)} [weight=${getEdgeWeight(node, succ)},color="${randomColorCSS(i++, 1, 0.75)}"]`);
        });

        node.deps.forEach(dep => {
          edges.push(`${getNodeId(dep)} -> ${nodeId} [weight=0,color="#dddddd",style=dashed]`);
        });
      }
    }

    this.sourceNodes.forEach(visit);

    pastChangesDuringUpdate.clear();

    return `
digraph {
  pack = true
  ranksep = 1
  
  ${nodes.join("\n  ")}
  
  ${edges.join("\n  ")}
}
    `;
  }
}
